import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { procurementAPI } from '../services/api';
import { ProcurementItem, ProcurementItemStatus, ProcurementPriority, ProcurementRequest, ProcurementStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showCustomAlert } from './CustomAlert';

type ItemRow = {
  key: string;
  requestDbId: string;
  requestId: string;
  requestStatus: ProcurementStatus;
  createdAt?: string;
  requestedByName: string;
  item: ProcurementItem;
};

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary';

const itemStatusOptions: Array<{ label: string; value: ProcurementItemStatus; severity: TagSeverity }> = [
  { label: 'Na čekanju', value: 'na_cekanju', severity: 'warning' },
  { label: 'U obradi', value: 'u_obradi', severity: 'info' },
  { label: 'Poručeno', value: 'poruceno', severity: 'secondary' },
  { label: 'Isporučeno', value: 'isporuceno', severity: 'success' },
  { label: 'Otkazano', value: 'otkazano', severity: 'danger' }
];

// Isporučene stavke nisu u tabeli, pa se po tom stanju ne filtrira
const statusFilterOptions = itemStatusOptions.filter((o) => o.value !== 'isporuceno');

const priorityOptions: Array<{ label: string; value: ProcurementPriority; severity: TagSeverity }> = [
  { label: 'Hitno', value: 'urgent', severity: 'danger' },
  { label: 'Visoka', value: 'high', severity: 'warning' },
  { label: 'Srednja', value: 'medium', severity: 'info' },
  { label: 'Niska', value: 'low', severity: 'secondary' }
];

const requestStatusLabel = (status: ProcurementStatus) => (status === 'approved' ? 'Odobren' : 'Na čekanju');

const appendToBody = typeof document !== 'undefined' ? document.body : undefined;

const statusOf = (item: ProcurementItem) => itemStatusOptions.find((o) => o.value === (item.itemStatus || 'na_cekanju')) || itemStatusOptions[0];
const priorityOf = (item: ProcurementItem) => priorityOptions.find((o) => o.value === (item.priority || 'medium')) || priorityOptions[2];
const safeLink = (link?: string) => (link && /^https?:\/\//i.test(link.trim()) ? link.trim() : '');
const shortUnit = (unit?: string) => (/^komad/i.test(unit || '') ? 'kom' : unit || '');
const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('sr-RS') : '');

const ProcurementItemsTable: React.FC = () => {
  const { user } = useAuth();
  const canUpdate = user?.role === 'logistika' || user?.role === 'admin';
  const toast = useRef<Toast>(null);

  const [requests, setRequests] = useState<ProcurementRequest[]>([]);
  // DataTable ne iscrtava ponovo redove koji se nisu menjali, pa njihovi handleri čitaju stanje preko ref-a
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProcurementItemStatus[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<ProcurementPriority[]>([]);
  const [noteRow, setNoteRow] = useState<ItemRow | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await procurementAPI.getAll();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Greška pri učitavanju zahteva');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Jedan red po stavci; isporučene stavke i isporučeni, verifikovani i odbijeni zahtevi više nisu u toku
  const allRows = useMemo<ItemRow[]>(
    () =>
      requests
        .filter((r) => r.status === 'pending' || r.status === 'approved')
        .flatMap((r) =>
          (r.items || [])
            .map((item, idx) => ({
              key: item._id || `${r._id}-${idx}`,
              requestDbId: r._id,
              requestId: r.requestId,
              requestStatus: r.status,
              createdAt: r.createdAt,
              requestedByName: `${r.requestedBy?.firstName || ''} ${r.requestedBy?.lastName || ''}`.trim(),
              item
            }))
            .filter((row) => row.item.itemStatus !== 'isporuceno')
        ),
    [requests]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (statusFilter.length && !statusFilter.includes(statusOf(row.item).value)) return false;
      if (priorityFilter.length && !priorityFilter.includes(priorityOf(row.item).value)) return false;
      if (!q) return true;
      return [row.requestId, row.item.productName, row.requestedByName].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [allRows, search, statusFilter, priorityFilter]);

  const showError = (detail: string) => toast.current?.show({ severity: 'error', summary: 'Greška', detail, life: 5000 });

  const mergeItem = (row: ItemRow, updated: ProcurementItem) =>
    setRequests((prev) =>
      prev.map((r) => (r._id === row.requestDbId ? { ...r, items: r.items.map((i) => (i._id === row.item._id ? { ...i, ...updated } : i)) } : r))
    );

  const openNote = (row: ItemRow) => {
    setNoteRow(row);
    setNoteText(row.item.itemNote || '');
  };

  const saveNote = async () => {
    if (!noteRow?.item._id) return;
    setNoteSaving(true);
    try {
      const updated = await procurementAPI.updateItemNote(noteRow.requestDbId, noteRow.item._id, noteText);
      mergeItem(noteRow, updated);
      setNoteRow(null);
    } catch (e: any) {
      showError(e?.response?.data?.message || 'Napomena nije sačuvana');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleStatusChange = (row: ItemRow, itemStatus: ProcurementItemStatus) => {
    const others = (requestsRef.current.find((r) => r._id === row.requestDbId)?.items || []).filter((i) => i._id !== row.item._id);
    const closesRequest = (itemStatus === 'isporuceno' || itemStatus === 'otkazano') && others.every((i) => i.itemStatus === 'isporuceno' || i.itemStatus === 'otkazano');
    const closedAs = itemStatus === 'otkazano' && others.every((i) => i.itemStatus === 'otkazano') ? 'odbijen' : 'isporučen';
    const closingText = closesRequest ? ` Ovo je poslednja otvorena stavka, pa će zahtev ${row.requestId} biti ${closedAs} i nestaće iz tabele.` : '';
    // Izmena koja sklanja stavke iz tabele ne može se ispraviti odavde, pa se prvo potvrđuje
    if (itemStatus === 'isporuceno') {
      showCustomAlert.confirm(
        `Označiti "${row.item.productName}" kao isporučeno? Stavka će nestati iz tabele.${closingText}`,
        () => void saveStatus(row, itemStatus),
        'Potvrda isporuke'
      );
      return;
    }
    if (closesRequest) {
      showCustomAlert.confirm(`Označiti "${row.item.productName}" kao otkazano?${closingText}`, () => void saveStatus(row, itemStatus), 'Potvrda otkazivanja');
      return;
    }
    void saveStatus(row, itemStatus);
  };

  const saveStatus = async (row: ItemRow, itemStatus: ProcurementItemStatus) => {
    if (!row.item._id || itemStatus === statusOf(row.item).value) return;
    setSavingKey(row.key);
    try {
      const { item, requestStatus } = await procurementAPI.updateItemStatus(row.requestDbId, row.item._id, itemStatus);
      mergeItem(row, item);
      if (requestStatus === 'delivered' || requestStatus === 'rejected') {
        setRequests((prev) => prev.map((r) => (r._id === row.requestDbId ? { ...r, status: requestStatus } : r)));
        toast.current?.show(
          requestStatus === 'delivered'
            ? { severity: 'success', summary: 'Zahtev isporučen', detail: `Zahtev ${row.requestId} je prešao u isporučeno.`, life: 5000 }
            : { severity: 'warn', summary: 'Zahtev odbijen', detail: `Sve stavke su otkazane, zahtev ${row.requestId} je odbijen.`, life: 5000 }
        );
      }
    } catch (e: any) {
      showError(e?.response?.data?.message || 'Stanje stavke nije sačuvano');
    } finally {
      setSavingKey('');
    }
  };

  const handleExport = async () => {
    const itemIds = rows.map((r) => r.item._id).filter(Boolean) as string[];
    if (!itemIds.length) return;
    const labels = (options: Array<{ label: string; value: string }>, values: string[]) =>
      options.filter((o) => values.includes(o.value)).map((o) => o.label).join(', ');
    const filterText = [
      search.trim() && `Pretraga: ${search.trim()}`,
      statusFilter.length && `Stanje: ${labels(itemStatusOptions, statusFilter)}`,
      priorityFilter.length && `Prioritet: ${labels(priorityOptions, priorityFilter)}`
    ]
      .filter(Boolean)
      .join(' · ');
    setExporting(true);
    try {
      const blob = await procurementAPI.downloadItemsTablePdf(itemIds, filterText.slice(0, 500) || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tabela-zahteva-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e: any) {
      showError('PDF nije mogao da se napravi');
    } finally {
      setExporting(false);
    }
  };

  const statusTag = (option: { label: string; severity: TagSeverity }) => <Tag value={option.label} severity={option.severity} />;

  const statusBody = (row: ItemRow) => {
    const current = statusOf(row.item);
    const updatedInfo = row.item.itemStatusUpdatedBy && (
      <small style={{ display: 'block', color: '#64748b', marginTop: 4 }}>
        {row.item.itemStatusUpdatedBy}, {formatDate(row.item.itemStatusUpdatedAt)}
      </small>
    );
    if (!canUpdate || !row.item._id) {
      return (
        <>
          {statusTag(current)}
          {updatedInfo}
        </>
      );
    }
    return (
      <>
        <Dropdown
          value={current.value}
          options={itemStatusOptions}
          optionLabel="label"
          optionValue="value"
          optionDisabled={(o: { value: ProcurementItemStatus }) => o.value === 'isporuceno' && row.requestStatus !== 'approved'}
          itemTemplate={statusTag}
          valueTemplate={(option) => (option ? statusTag(option) : null)}
          disabled={savingKey === row.key}
          appendTo={appendToBody}
          onChange={(e) => handleStatusChange(row, e.value)}
          style={{ width: '100%' }}
        />
        {updatedInfo}
      </>
    );
  };

  const noteBody = (row: ItemRow) => {
    const note = row.item.itemNote || '';
    if (!canUpdate && !note) return <span style={{ color: '#94a3b8' }}>—</span>;
    return (
      <>
        <Button
          label="Napomena"
          icon={note ? 'pi pi-comment' : 'pi pi-plus'}
          size="small"
          outlined={!note}
          severity={note ? 'info' : 'secondary'}
          onClick={() => openNote(row)}
        />
        {note && (
          <small
            title={note}
            style={{ display: 'block', color: '#64748b', marginTop: 4, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {note}
          </small>
        )}
      </>
    );
  };

  const linkBody = (row: ItemRow) => {
    const link = safeLink(row.item.link);
    if (!link) return <span style={{ color: '#94a3b8' }}>—</span>;
    return (
      <a href={link} target="_blank" rel="noopener noreferrer">
        <i className="pi pi-external-link" style={{ marginRight: 4 }} />
        Otvori
      </a>
    );
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Toast ref={toast} />
      <Card title="Tabela zahteva" subTitle="Sve stavke iz zahteva za nabavku i stanje svake stavke">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex flex-wrap align-items-center gap-2" style={{ marginBottom: '1rem' }}>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraga: stavka, broj zahteva, podnosilac"
            style={{ flex: '1 1 280px' }}
          />
          <MultiSelect
            value={statusFilter}
            options={statusFilterOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Sva stanja"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} stanja"
            appendTo={appendToBody}
            onChange={(e) => setStatusFilter(e.value || [])}
            style={{ flex: '0 1 200px' }}
          />
          <MultiSelect
            value={priorityFilter}
            options={priorityOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Svi prioriteti"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} prioriteta"
            appendTo={appendToBody}
            onChange={(e) => setPriorityFilter(e.value || [])}
            style={{ flex: '0 1 200px' }}
          />
          <Button icon="pi pi-refresh" text tooltip="Osveži" onClick={load} loading={loading} />
          <Button label="Izvezi PDF" icon="pi pi-file-pdf" outlined onClick={handleExport} loading={exporting} disabled={!rows.length} />
        </div>

        <DataTable
          value={rows}
          loading={loading}
          dataKey="key"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          size="small"
          stripedRows
          responsiveLayout="stack"
          breakpoint="768px"
          emptyMessage="Nema stavki"
        >
          <Column
            header="Zahtev"
            body={(r: ItemRow) => (
              <>
                <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.requestId}</div>
                <small style={{ color: '#64748b' }}>
                  {formatDate(r.createdAt)} · {requestStatusLabel(r.requestStatus)}
                </small>
              </>
            )}
            style={{ width: '170px' }}
          />
          <Column header="Podneo" field="requestedByName" style={{ width: '160px' }} />
          <Column header="Stavka" body={(r: ItemRow) => r.item.productName} />
          <Column
            header="Količina"
            body={(r: ItemRow) => <span style={{ whiteSpace: 'nowrap' }}>{`${r.item.quantity} ${shortUnit(r.item.unit)}`}</span>}
            style={{ width: '100px' }}
          />
          <Column
            header="Prioritet"
            body={(r: ItemRow) => {
              const p = priorityOf(r.item);
              return <Tag value={p.label} severity={p.severity} />;
            }}
            style={{ width: '100px' }}
          />
          <Column header="Link" body={linkBody} style={{ width: '90px' }} />
          <Column header="Stanje" body={statusBody} style={{ width: '200px' }} />
          <Column header="Napomena" body={noteBody} style={{ width: '190px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={noteRow ? `Napomena: ${noteRow.item.productName}` : 'Napomena'}
        visible={!!noteRow}
        style={{ width: '520px', maxWidth: '96vw' }}
        onHide={() => setNoteRow(null)}
        modal
      >
        {noteRow && (
          <div className="p-fluid">
            <div style={{ color: '#64748b', marginBottom: '0.75rem' }}>{noteRow.requestId}</div>
            {canUpdate ? (
              <InputTextarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={5} maxLength={1000} autoFocus autoResize />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap' }}>{noteRow.item.itemNote}</div>
            )}
            <div className="flex gap-2 justify-content-end" style={{ marginTop: '1rem' }}>
              {canUpdate && <Button label="Sačuvaj" icon="pi pi-check" onClick={saveNote} loading={noteSaving} />}
              <Button label={canUpdate ? 'Otkaži' : 'Zatvori'} icon="pi pi-times" severity="secondary" outlined onClick={() => setNoteRow(null)} />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ProcurementItemsTable;
